import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Building2, User, Phone, Target, Thermometer, Send, CheckCircle2, Info, ShieldAlert } from "lucide-react";
import api, { formatErr } from "@/lib/api";

/**
 * Public visitor registration form. Rendered at `/v/:siteId` — no auth
 * required. Bilingual (EN + 中文). After submission, tenant staff verify
 * on the tenant-side "Visitor Registration" page.
 */
export default function PublicVisitorForm() {
  const { siteId } = useParams();
  const [ctx, setCtx] = useState(null);       // {site, tenant, sections, ...}
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);   // {id} once done

  // form state
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [purpose, setPurpose] = useState("");
  const [temperature, setTemperature] = useState("");
  const [answers, setAnswers] = useState({});     // { item_key: { answer, reason } }
  const [ackInstructions, setAckInstructions] = useState(false);
  const [ackPdpa, setAckPdpa] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/public/visitor-form/${siteId}`);
        setCtx(data);
      } catch (e) {
        setErr(formatErr(e.response?.data?.detail) || "This QR code isn't recognised.");
      } finally { setLoading(false); }
    })();
  }, [siteId]);

  const allItems = (ctx?.sections || []).flatMap((s) => s.items || []);
  const setAnswer = (key, patch) =>
    setAnswers((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));

  const canSubmit = (() => {
    if (submitting) return false;
    if (!company.trim() || !name.trim() || !mobile.trim() || !purpose.trim()) return false;
    if (!ackInstructions || !ackPdpa) return false;
    for (const it of allItems) {
      const a = answers[it.key];
      if (!a?.answer) return false;
      if (a.answer === "yes" && it.requires_reason && !(a.reason || "").trim()) return false;
    }
    return true;
  })();

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        company: company.trim(),
        name: name.trim(),
        mobile: mobile.trim(),
        purpose: purpose.trim(),
        temperature: temperature.trim() || null,
        acknowledge_instructions: ackInstructions,
        consent_pdpa: ackPdpa,
        answers: allItems.map((it) => ({
          item_key: it.key,
          answer: answers[it.key]?.answer || "no",
          reason: (answers[it.key]?.reason || "").trim(),
        })),
      };
      const { data } = await api.post(`/public/visitor-form/${siteId}`, payload);
      setSubmitted({ id: data.id });
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Couldn't submit. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}><div className="opacity-60">Loading…</div></div>
      </div>
    );
  }

  if (err && !ctx) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div className="flex items-center gap-2 mb-2" style={{ color: "#b91c1c" }}>
            <ShieldAlert size={18} /> <strong>Unable to open form</strong>
          </div>
          <p className="text-sm opacity-80 m-0">{err}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle} data-testid="visitor-form-success">
          <div className="flex items-center gap-2 mb-3" style={{ color: "#166534" }}>
            <CheckCircle2 size={20} />
            <strong className="text-lg">Thank you — form submitted</strong>
          </div>
          <p className="text-sm opacity-80 m-0 mb-2">
            <strong>谢谢您 — 表格已提交</strong>
          </p>
          <p className="text-sm opacity-80 m-0 mb-3">
            Please hand the device back to the staff so they can complete the sign-off before you enter the premises.
          </p>
          <p className="text-xs opacity-60 m-0">
            请把设备交还给员工，让他们在您进入场所之前完成签核。
          </p>
          <div className="mt-4 text-xs opacity-60">Reference: <code>{submitted.id.slice(0, 8)}</code></div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle} data-testid="public-visitor-form">
      <div style={cardStyle}>
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-wider opacity-60">{ctx.form_ref}</div>
          <h1 className="m-0" style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>
            {ctx.title}
          </h1>
          <div className="text-sm opacity-60 mt-0.5">访客登记表</div>
          <div className="text-xs opacity-70 mt-2">
            {ctx.tenant?.name} <span className="opacity-40">·</span> {ctx.site?.name}
          </div>
        </div>

        {/* Visitor details */}
        <FieldRow icon={Building2} label="Company" labelZh="公司">
          <input className="vb-input" value={company} onChange={(e) => setCompany(e.target.value)}
            data-testid="v-company" placeholder="Company name" />
        </FieldRow>
        <FieldRow icon={User} label="Name" labelZh="姓名">
          <input className="vb-input" value={name} onChange={(e) => setName(e.target.value)}
            data-testid="v-name" placeholder="Your full name" />
        </FieldRow>
        <FieldRow icon={Phone} label="Mobile No" labelZh="联络号码">
          <input className="vb-input" value={mobile} onChange={(e) => setMobile(e.target.value)}
            data-testid="v-mobile" placeholder="Mobile number" inputMode="tel" />
        </FieldRow>
        <FieldRow icon={Target} label="Purpose of Visit" labelZh="参观原因">
          <input className="vb-input" value={purpose} onChange={(e) => setPurpose(e.target.value)}
            data-testid="v-purpose" placeholder="e.g. Delivery, audit, meeting" />
        </FieldRow>
        <FieldRow icon={Thermometer} label="Temperature (optional)" labelZh="体温（可选）">
          <input className="vb-input" value={temperature} onChange={(e) => setTemperature(e.target.value)}
            data-testid="v-temperature" placeholder="e.g. 36.5 °C" />
        </FieldRow>

        {/* Checklist */}
        <div className="mt-5 mb-2">
          <div className="text-[13px] font-semibold" style={{ color: "#0f172a" }}>Health Screening</div>
          <div className="text-xs opacity-60">健康筛查</div>
        </div>

        {ctx.sections.map((s) => (
          <div key={s.name}>
            {s.items.map((it) => (
              <VisitorItem key={it.key} item={it}
                value={answers[it.key]}
                onChange={(patch) => setAnswer(it.key, patch)} />
            ))}
          </div>
        ))}

        {/* Instructions acknowledgement */}
        <div className="mt-4 p-3 rounded" style={{ background: "#fef3c7", border: "1px solid #fde68a" }}>
          <div className="flex items-start gap-2">
            <Info size={16} style={{ color: "#92400e", flexShrink: 0, marginTop: 2 }} />
            <div className="text-[13px]" style={{ color: "#78350f" }}>
              <div><em>Please remove all jewellery e.g. earrings, necklaces, bracelets, rings, watches and put on the apparel and shoes cover provided prior to your visit to the production premises. Thank you.</em></div>
              <div className="mt-1 opacity-80">请摘下所有珠宝，例如耳环，项链，手镯，戒指，手表，并在进入生产部之前穿上所提供的服装和鞋套。谢谢。</div>
            </div>
          </div>
          <label className="flex items-center gap-2 mt-3 text-[13px]" data-testid="v-ack-instructions">
            <input type="checkbox" checked={ackInstructions} onChange={(e) => setAckInstructions(e.target.checked)} />
            <span>I have read and understood the instructions above. <span className="opacity-60">我已阅读并理解以上须知</span></span>
          </label>
        </div>

        {/* PDPA consent */}
        <label className="flex items-start gap-2 mt-3 text-[12px] opacity-80" data-testid="v-ack-pdpa">
          <input type="checkbox" checked={ackPdpa} onChange={(e) => setAckPdpa(e.target.checked)} className="mt-1" />
          <span>
            I consent to my details being held solely for food-safety audit purposes.
            <span className="opacity-60"> 我同意本次记录的资料仅用于食品安全审核用途。</span>
          </span>
        </label>

        {err && <div className="mt-3 p-2 rounded text-[12px]" style={{ background: "#fee2e2", color: "#991b1b" }}>{err}</div>}

        <button
          className="mt-5 w-full flex items-center justify-center gap-2 py-3 rounded"
          style={{
            background: canSubmit ? "#0f766e" : "#94a3b8",
            color: "white", fontWeight: 600, border: "none",
            cursor: canSubmit ? "pointer" : "not-allowed",
          }}
          disabled={!canSubmit}
          onClick={submit}
          data-testid="v-submit"
        >
          <Send size={16} /> {submitting ? "Submitting…" : "Submit / 提交"}
        </button>
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
  padding: "24px 12px",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
};

const cardStyle = {
  background: "white",
  borderRadius: 12,
  padding: 20,
  width: "100%",
  maxWidth: 560,
  boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
};

function FieldRow({ icon: Icon, label, labelZh, children }) {
  return (
    <div className="mb-3">
      <label className="flex items-center gap-1.5 text-[12px] font-medium mb-1" style={{ color: "#0f172a" }}>
        <Icon size={13} style={{ opacity: 0.7 }} />
        <span>{label}</span>
        <span className="opacity-50 font-normal">· {labelZh}</span>
      </label>
      {children}
    </div>
  );
}

function VisitorItem({ item, value, onChange }) {
  const showReason = value?.answer === "yes" && item.requires_reason;
  return (
    <div className="p-3 mb-2 rounded" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}
      data-testid={`v-item-${item.key}`}>
      <div className="text-[13px] font-medium" style={{ color: "#0f172a" }}>{item.label}</div>
      {item.label_zh && (
        <div className="text-[12px] opacity-60 mt-0.5">{item.label_zh}</div>
      )}
      <div className="flex gap-2 mt-2">
        {[
          { v: "no",  label: "No",  zh: "否" },
          { v: "yes", label: "Yes", zh: "是" },
        ].map((opt) => {
          const on = value?.answer === opt.v;
          return (
            <button key={opt.v} type="button"
              onClick={() => onChange({ answer: opt.v })}
              data-testid={`v-answer-${item.key}-${opt.v}`}
              style={{
                flex: 1, padding: "8px 10px", borderRadius: 8, fontWeight: 600,
                background: on ? (opt.v === "yes" ? "#b91c1c" : "#0f766e") : "white",
                color: on ? "white" : "#0f172a",
                border: `1px solid ${on ? "transparent" : "#cbd5e1"}`,
                cursor: "pointer", fontSize: 13,
              }}>
              {opt.label} <span style={{ opacity: 0.7, fontWeight: 400 }}>· {opt.zh}</span>
            </button>
          );
        })}
      </div>
      {showReason && (
        <div className="mt-2">
          <label className="text-[11px] opacity-70">Please specify · 请说明</label>
          <input className="vb-input" value={value?.reason || ""}
            onChange={(e) => onChange({ reason: e.target.value })}
            data-testid={`v-reason-${item.key}`}
            placeholder="Details required" />
        </div>
      )}
    </div>
  );
}
